import {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { isInkOnlyRoute } from '@/utils/routeAppearance';

type Theme = 'system' | 'light' | 'dark';
type EffectiveTheme = 'light' | 'dark';

const THEME_KEY = 'journal-party-theme';
const SYSTEM_THEME_QUERY = '(prefers-color-scheme: dark)';
const THEME_COLORS = { light: '#F7F3EA', dark: '#010409' } as const;

interface AndroidAppearancePlugin {
  setTheme: (appearance: { effectiveTheme: EffectiveTheme }) => Promise<void>;
}
let androidAppearance: AndroidAppearancePlugin | undefined;

type NativeAppearanceWindow = Window & {
  webkit?: {
    messageHandlers?: {
      jpAppearance?: { postMessage: (appearance: { theme: Theme; effectiveTheme: EffectiveTheme; forcedTheme?: 'dark' }) => void };
    };
  };
};

function readPreference(): Theme {
  try {
    const value = window.localStorage.getItem(THEME_KEY);
    return value === 'light' || value === 'dark' || value === 'system' ? value : 'system';
  } catch {
    return 'system';
  }
}

function resolveTheme(theme: Theme, systemIsDark: boolean): EffectiveTheme {
  return theme === 'system' ? (systemIsDark ? 'dark' : 'light') : theme;
}

function applyTheme(theme: Theme, effectiveTheme: EffectiveTheme, forcedInk = false) {
  const root = document.documentElement;
  const color = THEME_COLORS[effectiveTheme];
  root.classList.toggle('dark', effectiveTheme === 'dark');
  root.dataset.jpTheme = effectiveTheme === 'dark' ? 'ink' : 'paper';
  root.dataset.resolvedTheme = effectiveTheme;
  root.style.colorScheme = effectiveTheme;
  root.style.backgroundColor = color;
  root.style.setProperty('--jp-shell-background', color);
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', color);

  // The iOS bridge owns native status text and the WKWebView background.
  // Browser builds and older native binaries simply have no handler.
  try {
    (window as NativeAppearanceWindow).webkit?.messageHandlers?.jpAppearance?.postMessage({
      theme, effectiveTheme, ...(forcedInk ? { forcedTheme: 'dark' as const } : {}),
    });
  } catch {
    // A native bridge can disappear while a page is being replaced.
  }

  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
    androidAppearance ??= registerPlugin<AndroidAppearancePlugin>('AppAppearance');
    // Only the resolved theme crosses the bridge. The shared provider still
    // owns System, saved choices, and onboarding's temporary Ink appearance.
    void androidAppearance.setTheme({ effectiveTheme }).catch(() => {
      // Older installed binaries may not have the appearance plugin yet.
    });
  }
}

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  effectiveTheme: EffectiveTheme;
  syncRouteAppearance: (pathname: string, search: string) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** One app-wide appearance preference. System follows device appearance changes. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  // Keep SSR and the first hydration render identical. The inline head bootstrap
  // applies the actual appearance before paint; this effect then adopts it.
  const [appearance, setAppearance] = useState<{ theme: Theme; effectiveTheme: EffectiveTheme }>({
    theme: 'system',
    effectiveTheme: 'light',
  });
  const preferenceRef = useRef<Theme | null>(null);
  const mediaQueryRef = useRef<MediaQueryList | null>(null);
  const routeRef = useRef<{ pathname: string; search: string } | null>(null);

  const resolveAppearance = useCallback((theme: Theme) => {
    const route = routeRef.current ?? window.location;
    const forcedInk = isInkOnlyRoute(route.pathname, route.search, document.documentElement.classList.contains('native-runtime'));
    const systemIsDark = mediaQueryRef.current?.matches ?? window.matchMedia?.(SYSTEM_THEME_QUERY).matches ?? false;
    const effectiveTheme = forcedInk ? 'dark' : resolveTheme(theme, systemIsDark);
    applyTheme(theme, effectiveTheme, forcedInk);
    return effectiveTheme;
  }, []);

  const updateAppearance = useCallback((theme: Theme) => {
    preferenceRef.current = theme;
    const effectiveTheme = resolveAppearance(theme);
    setAppearance((previous) => previous.theme === theme && previous.effectiveTheme === effectiveTheme
      ? previous
      : { theme, effectiveTheme });
  }, [resolveAppearance]);

  const syncRouteAppearance = useCallback((pathname: string, search: string) => {
    routeRef.current = { pathname, search };
    // Apply document/native colors now, but let lazy SSR routes finish hydrating
    // before React replaces their appearance-dependent content.
    startTransition(() => updateAppearance(preferenceRef.current ?? readPreference()));
  }, [updateAppearance]);

  useEffect(() => {
    const mediaQuery = typeof window.matchMedia === 'function' ? window.matchMedia(SYSTEM_THEME_QUERY) : null;
    mediaQueryRef.current = mediaQuery;
    const initialTheme = preferenceRef.current ?? readPreference();
    preferenceRef.current = initialTheme;
    const initialEffectiveTheme = resolveAppearance(initialTheme);
    startTransition(() => setAppearance({ theme: initialTheme, effectiveTheme: initialEffectiveTheme }));

    const onSystemChange = () => {
      if (preferenceRef.current === 'system') updateAppearance('system');
    };
    const onStorageChange = (event: StorageEvent) => {
      if (event.key === THEME_KEY || event.key === null) updateAppearance(readPreference());
    };
    // iOS may suspend web events while backgrounded across an appearance schedule.
    const onVisible = () => {
      if (document.visibilityState === 'visible') onSystemChange();
    };
    mediaQuery?.addEventListener('change', onSystemChange);
    window.addEventListener('storage', onStorageChange);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onSystemChange);
    return () => {
      mediaQuery?.removeEventListener('change', onSystemChange);
      window.removeEventListener('storage', onStorageChange);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onSystemChange);
      mediaQueryRef.current = null;
    };
  }, [resolveAppearance, updateAppearance]);

  const setTheme = useCallback((theme: Theme) => {
    try {
      window.localStorage.setItem(THEME_KEY, theme);
    } catch {
      // Private/restricted storage must not prevent changing appearance.
    }
    updateAppearance(theme);
  }, [updateAppearance]);

  const value = useMemo(() => ({ ...appearance, setTheme, syncRouteAppearance }), [appearance, setTheme, syncRouteAppearance]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext) ?? {
    theme: 'system',
    effectiveTheme: 'light',
    setTheme: () => {},
    syncRouteAppearance: () => {},
  };
}
